import type { ImplementedCopilotFeatures, CopilotSettingsStore } from 'owa-copilot-settings-store';
import { getApplicationSettings } from 'owa-application-settings';
import isCopilotFeatureEnabled from './isCopilotFeatureEnabled';
import createStoreCheckForGenericEnablement from './createStoreCheckForGenericEnablement';
import type { MailboxInfo } from 'owa-client-types';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { isEventDraftAssistantEnabled } from './isEventDraftAssistantEnabled';

type ElaborateScenarios = Extract<ImplementedCopilotFeatures, 'Elaborate'>;

const isCalendarElaborateEnabled: (
    scenario: 'Elaborate',
    mailboxInfo: MailboxInfo,
    skipLanguageCheck?: boolean,
    shouldIgnoreUserPreferences?: boolean
) => boolean | undefined =
    // eslint-disable-next-line owa-custom-rules/no-side-effects -- BASELINE, DO NOT COPY-PASTE
    createStoreCheckForGenericEnablement<ElaborateScenarios, boolean>(
        'CalendarElaborate',
        isCalendarElaborateEnabledInternal
    );

export default isCalendarElaborateEnabled;

function isCalendarElaborateEnabledInternal(
    store: CopilotSettingsStore,
    _language: string,
    scenario: ElaborateScenarios,
    mailboxInfo: MailboxInfo,
    skipLanguageCheck?: boolean,
    skipGroupMailboxCheck?: boolean
): boolean {
    // Feature is not supported for Group Mailboxes
    if (!skipGroupMailboxCheck && mailboxInfo.type === 'GroupMailbox') {
        return false;
    }

    if (store.isEnabled) {
        const featureEnabled = store.featureInfo.get(scenario);
        if (featureEnabled?.isEnabled) {
            const appSettings = getApplicationSettings('Copilot', mailboxInfo);
            return (
                !!isCopilotFeatureEnabled(
                    scenario,
                    mailboxInfo,
                    skipLanguageCheck ?? appSettings.enableMultiLanguage
                ) &&
                isGlobalFeatureEnabled('time-copilot-calendar-draft-polaris') &&
                appSettings.calendarElaborateEnabled && // This is controlled through the Killswitch ECS here: https://ecs.skype.com/Substrate/OutlookWebClient/configurations/1092852
                !!featureEnabled.capabilities &&
                'calendarElaborate' in featureEnabled.capabilities &&
                featureEnabled.capabilities.calendarElaborate
            );
        }
    }
    return false;
}

/**
 * Checks if Calendar Draft Agenda (Elaborate) is enabled for the given event.
 *
 * By default, this feature is only available for new events. However, with the
 * draft assistant feature flag enabled, it also supports existing events
 * being edited by the organizer.
 *
 * @param scenario - The scenario to check (should be 'Elaborate').
 * @param mailboxInfo - The mailbox information.
 * @param event - The calendar event to check. For new events, event.ItemId should be undefined.
 * @param skipLanguageCheck - Optional flag to skip language validation.
 * @param shouldIgnoreUserPreferences - Optional flag to ignore user preferences.
 * @param isProtectiveAutoSaveOnly - Optional flag indicating the event was only auto-saved for protective purposes (e.g., add-ins).
 * @returns true if the feature should be enabled for this event, false otherwise.
 */
export function isCalendarElaborateEnabledForEvent(
    scenario: 'Elaborate',
    mailboxInfo: MailboxInfo,
    event?: CalendarEvent,
    skipLanguageCheck?: boolean,
    shouldIgnoreUserPreferences?: boolean,
    isProtectiveAutoSaveOnly?: boolean
): boolean {
    // Draft Assistant (BizChat sidepane) fully replaces the inline elaborate experience.
    // When active, DraftAgendaPlugin + DraftRewritePlugin handle drafting instead.
    if (isEventDraftAssistantEnabled(mailboxInfo)) {
        return false;
    }

    // Feature is only for new events, not existing events being edited
    // Exception: Allow if the event was only auto-saved for protective purposes (e.g., add-ins)
    if (event && event.ItemId !== undefined && event.ItemId !== null && !isProtectiveAutoSaveOnly) {
        return false;
    }

    // Check the general feature enablement
    return (
        isCalendarElaborateEnabled(
            scenario,
            mailboxInfo,
            skipLanguageCheck,
            shouldIgnoreUserPreferences
        ) ?? false
    );
}
